import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {Map<string, number>} */
  pendingOptimisticUpdates = new Map();

  /** @type {number} */
  get currentCartCount() {
    const parsedCount = parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
    return Number.isFinite(parsedCount) ? parsedCount : 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '99+';
    this.refs.cartBubble.toggleAttribute('data-maintain-ratio', value > 0 && value <= 99);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener(ThemeEvents.cartAddPending, this.onPendingCartAdd);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener(ThemeEvents.cartAddPending, this.onPendingCartAdd);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * @param {number} value
   * @param {boolean} animate
   */
  updateCartBubbleCount = async (value, animate = true) => {
    const nextCount = Math.max(0, Number.isFinite(value) ? value : 0);

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCount === 0);
    this.currentCartCount = nextCount;
    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || nextCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * @param {CustomEvent<{ token?: string, quantity?: number }>} event
   */
  onPendingCartAdd = (event) => {
    const token = event.detail?.token;
    const quantity = Number(event.detail?.quantity) || 1;

    if (token) {
      this.pendingOptimisticUpdates.set(token, quantity);
    }

    this.updateCartBubbleCount(this.currentCartCount + quantity);
  };

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const exactItemCount = event.detail.resource?.item_count;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    const didError = event.detail.data?.didError === true;
    const optimisticToken = event.detail.data?.optimisticToken;

    if (optimisticToken && this.pendingOptimisticUpdates.has(optimisticToken)) {
      const pendingQuantity = this.pendingOptimisticUpdates.get(optimisticToken) || 0;
      this.pendingOptimisticUpdates.delete(optimisticToken);

      if (didError) {
        this.updateCartBubbleCount(this.currentCartCount - pendingQuantity, false);
        return;
      }
    }

    if (didError) return;

    if (exactItemCount !== undefined) {
      this.updateCartBubbleCount(exactItemCount, false);
      return;
    }

    if (comingFromProductForm) {
      this.updateCartBubbleCount(this.currentCartCount + itemCount, false);
      return;
    }

    this.updateCartBubbleCount(itemCount, false);
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.updateCartBubbleCount(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
